import React, { Dispatch, SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useHorizontalWheelScroll, useTranslation } from "utils";

import { CROP_TYPE_FCPO, CropFCPO, CropsWithMissingHistoricalPracticeDataByYear, DocScanFieldRecord, FieldListField, PRACTICE_SLUG } from "models";

import { AlertBanner, Flex, Typography, getDocFieldCrops } from "components";

import { getDocScanFieldId } from "pages/app/document-scan/utils/dateEntryUtils";
import { MissingDataTable } from "pages/app/historical-practice-data-table-view/components/MissingDataTable";
import type { HistoricalPracticeFiltersState } from "pages/app/historical-practice-data-table-view/components/PracticeFilters";

const DEFAULT_FILTERS: HistoricalPracticeFiltersState = {
  appliedCropIds: [],
  previouslyAppliedCropIds: [],
  missingGrowingPeriod: false,
  missingOrganicFertilizers: false,
  missingSeedings: false,
  missingTillages: false,
};

type DateEntryStepProps = {
  eligibleRows: DocScanFieldRecord[];
  selectedYear: number | undefined;
  fieldDates: Map<string, { seedingStart: string; seedingEnd: string; harvestStart: string; harvestEnd: string }>;
  onFieldDatesChange: Dispatch<SetStateAction<Map<string, { seedingStart: string; seedingEnd: string; harvestStart: string; harvestEnd: string }>>>;
};

const ANT_TABLE_HEADER_PX = 56;

export const DateEntryStep = ({ eligibleRows, selectedYear, fieldDates, onFieldDatesChange }: DateEntryStepProps) => {
  const { t } = useTranslation("docScans");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const cropFilterChipsRowRef = useRef<HTMLDivElement>(null);
  const [tableBodyHeight, setTableBodyHeight] = useState(400);
  const [selectedCrops, setSelectedCrops] = useState<string[]>([]);

  useLayoutEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setTableBodyHeight(Math.max(300, entry.contentRect.height - ANT_TABLE_HEADER_PX));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setSelectedCrops([]);
  }, [eligibleRows]);

  const { cropsByYear, fields, uniqueCropNames } = useMemo(() => {
    const byYear: CropsWithMissingHistoricalPracticeDataByYear = {};
    const fieldList: FieldListField[] = [];

    eligibleRows.forEach((r) => {
      // The scan covers a single reporting year (docScan.year, passed in as selectedYear). All of a
      // field's scanned crops belong to it — don't rely on per-crop crop_year, which is unset for need_review.
      const docCrops = getDocFieldCrops(r.docFieldRaw);
      if (!docCrops.length || selectedYear === undefined) return;
      const year = selectedYear;

      const cropName = docCrops.map((c) => c.crop_name).join(", ");

      const savedDates = fieldDates.get(r.id);
      const fakeCrop: CropFCPO = {
        id: r.id,
        cropId: cropName,
        cropName,
        cropType: CROP_TYPE_FCPO.MAIN,
        harvestStartDate: savedDates?.harvestStart ?? null,
        harvestEndDate: savedDates?.harvestEnd ?? null,
        seedingStartDate: savedDates?.seedingStart ?? null,
        seedingEndDate: savedDates?.seedingEnd ?? null,
        seedingPracticeType: null,
        seedingVerificationStatus: null,
        hasResidue: null,
        isConfirmed: null,
        translationSlug: cropName,
        yieldPerHa: null,
        yieldPerHaUnit: null,
        fallbackDates: { seedingStartDate: null, harvestEndDate: null },
      };

      (byYear[year] ??= []).push({
        fieldId: getDocScanFieldId(r)!,
        crop: fakeCrop,
        hasMissingData: true,
        isTillageDepthRequired: false,
        assignedPractices: {
          [PRACTICE_SLUG.TILLAGES]: null,
          [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: null,
          [PRACTICE_SLUG.SEEDINGS]: null,
        },
      });

      if (r.farmFieldRaw) {
        let location = { lat: 0, lng: 0 };
        try {
          if (r.farmFieldRaw.location) location = JSON.parse(r.farmFieldRaw.location);
        } catch {
          // use default
        }
        fieldList.push({
          id: getDocScanFieldId(r)!,
          name: r.farmFieldRaw.name,
          size: r.farmFieldRaw.size,
          location,
          isInRedzone: false,
          regionId: "",
          redzones: [],
        });
      }
    });

    const uniqueCropNames = [
      ...new Set(
        eligibleRows
          .map((r) =>
            getDocFieldCrops(r.docFieldRaw)
              .map((c) => c.crop_name)
              .join(", ")
          )
          .filter(Boolean)
      ),
    ];

    return { cropsByYear: byYear, fields: fieldList, uniqueCropNames };
  }, [eligibleRows, fieldDates, selectedYear]);

  const isCropFilterShown = uniqueCropNames.length > 1;
  useHorizontalWheelScroll(cropFilterChipsRowRef, isCropFilterShown);

  const handleDatesCollected = (key: string, seedingStart: string, seedingEnd: string, harvestStart: string, harvestEnd: string) => {
    onFieldDatesChange((prev) => new Map(prev).set(key, { seedingStart, seedingEnd, harvestStart, harvestEnd }));
  };

  return (
    <div className="doc-scan-content wide" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", paddingBottom: 0 }}>
      <Flex vertical gap="1rem" className="margin-bottom-200">
        <Typography variant="h3">{t("dateEntry.heading")}</Typography>
        <Typography variant="paragraph-m" color="grey-600">
          {t("dateEntry.subtext")}
        </Typography>
        <AlertBanner type="recommended" iconName="tabler/bulb" iconAlign="start" description={t("dateEntry.bulkTip")} />
      </Flex>

      {isCropFilterShown && (
        <Flex align="center" gap="0.5rem" className="doc-scan-date-entry-filter-bar">
          <Typography variant="paragraph-s" color="grey-600" className="doc-scan-crop-filter-label">
            {t("dateEntry.filterBy")}
          </Typography>
          <div ref={cropFilterChipsRowRef} className="doc-scan-crop-filter-chips-row">
            {uniqueCropNames.map((name) => (
              <button
                key={name}
                type="button"
                className={`doc-scan-crop-filter-chip${selectedCrops.includes(name) ? " selected" : ""}`}
                onClick={() => setSelectedCrops((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]))}
              >
                {name}
              </button>
            ))}
          </div>
          {selectedCrops.length > 0 && (
            <button type="button" className="doc-scan-crop-filter-clear" onClick={() => setSelectedCrops([])}>
              {t("dateEntry.clearFilter")}
            </button>
          )}
        </Flex>
      )}

      <div ref={tableContainerRef} className="doc-scan-date-entry-table-container" style={{ flex: 1, minHeight: 0 }}>
        <MissingDataTable
          cropsWithMissingHistoricalPracticeDataByYear={cropsByYear}
          fields={fields}
          requiredPracticeSlugs={[]}
          selectedYear={selectedYear}
          filters={{ ...DEFAULT_FILTERS, appliedCropIds: selectedCrops }}
          tableBodyHeight={tableBodyHeight}
          onDatesCollected={handleDatesCollected}
          skipDbMutation={true}
          hidePracticeButtons={true}
          hideCropSubtext={true}
          clearSelectionsOnSubmit={true}
        />
      </div>
    </div>
  );
};
